import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { IPostTargetIdentity } from '../../models/utils/socialUtils';
import { useUserSession } from '../../models/hooks/appStateHooks';

export function usePruneSocialPostTargetMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (post: Api.ISocialMediaPost) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	return useMutation({
		mutationFn: (params: {
			postId: string;
			target: IPostTargetIdentity;
			impersonationContext?: Api.IImpersonationContext;
		}) => {
			return userSession.webServiceHelper.callAsync<Api.ISocialMediaPost>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext: params.impersonationContext,
					urlPath: `social/post/${encodeURIComponent(params.postId)}/pruneTarget`,
				}),
				'POST',
				params.target
			);
		},
		onError,
		onSuccess,
	});
}

export function getPruneSocialPostTargetErrorMessage(error?: Api.IOperationResultNoValue) {
	if (error?.systemCode === 403) {
		return 'You do not have permission to change this post.';
	}
	if (error?.systemCode === 404) {
		return 'That account is no longer on this post. Refresh to see its current accounts.';
	}
	// The endpoint rejects a still-connected target, a post that can no longer be edited and the post's
	// last remaining target with copy written for the customer, so pass those through as-is.
	return error?.systemMessage || 'Unable to remove this account from the post.';
}
